import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import * as React from 'react';
import {Input, type InputOnChangeData} from 'semantic-ui-react';

import {parseFancyExpression} from '../util/expr';
import {useDebouncedValue} from '../util/useDebouncedValue';

interface PanelExpressionInputProps {
  expression: string;
  placeholder: string;
  onChange: (value: string) => void;
  onRemove?: () => void;
}

/**
 * Input component for panel expressions with cursor position preservation.
 *
 * Problem: When using a fully controlled input where onChange immediately updates
 * the parent state, frequent parent re-renders cause React to lose track of the
 * cursor position, resulting in the cursor jumping to the end of the input on
 * every keystroke.
 *
 * Solution: Uses the useDebouncedValue hook to maintain local state and debounce
 * the onChange callback to the parent (350ms delay). The instantSetValue function
 * updates both local state and triggers the debounced parent callback, which:
 * - Keeps the input responsive by updating local value immediately
 * - Prevents cursor jumping by reducing parent re-renders during typing
 * - Maintains real-time validation against the local value
 * - Syncs external prop changes to local state via the hook's initialValue
 * - Improves performance by batching state updates to the parent
 */
export const PanelExpressionInput = ({
  expression,
  placeholder,
  onChange,
  onRemove,
}: PanelExpressionInputProps) => {
  const onChangeCallback = React.useCallback(onChange, [onChange]);

  const {
    value: localValue,
    debouncedSetValue: setLocalValue,
    flush,
  } = useDebouncedValue<string>(expression, 350, onChangeCallback);

  const handleChange = React.useCallback(
    (_: React.SyntheticEvent, {value}: InputOnChangeData) => {
      setLocalValue(value);
    },
    [setLocalValue]
  );

  return (
    <div className="expression">
      <Input
        error={localValue !== '' && parseFancyExpression(localValue) == null}
        className="expression-input"
        placeholder={placeholder}
        fluid
        value={localValue}
        onChange={handleChange}
        onBlur={flush}
      />
      {onRemove && (
        <LegacyWBIcon
          title=""
          className="expression-close"
          size="large"
          name="close"
          onClick={onRemove}
        />
      )}
    </div>
  );
};
